import React from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { ExternalLink, Edit, Users, Home, Layers, CheckCircle, Play, Square, Info } from 'lucide-react';
import { BrickWall, Grid2X2 } from "lucide-react";

// import "./ContextMenu.css";

const AssignmentContextMenu = ({
    children,
    assignment,
    onOpenAssignment,
    onEditAssignment,
    onAssignDesigner,
    onStartProduction,
    onEndProduction,
    availableDesigners = [],
    page = 'editor'
}) => {
    // Check if this is a separated view assignment
    const isSeparated = assignment._quoteType !== null && assignment._quoteType !== undefined;
    const separatedQuoteType = assignment._quoteType;

    const hasQuoteType = (type) => {
        return assignment.quotes?.[type]?.isRequired;
    };

    const getCurrentDesigner = (quoteType) => {
        return assignment.quotes?.[quoteType]?.designers?.[0];
    };

    const isDesignCompleted = (quoteType) => {
        return assignment.quotes?.[quoteType]?.designStatus === 'completed';
    };

    const getProductionStatus = () => {
        // For separated view, check specific quote type
        if (isSeparated) {
            const production = assignment?.quotes?.[separatedQuoteType]?.production; // CHANGED
            if (!production || !production.phase) return "not_started";
            return production.phase;
        }

        // For combined view, return most advanced phase
        const requiredQuotes = ['roof', 'wall', 'floor'].filter(type =>
            assignment.quotes?.[type]?.isRequired
        );

        const phases = requiredQuotes.map(type =>
            assignment.quotes?.[type]?.production?.phase || 'not_started' // CHANGED
        );

        if (phases.includes('completed')) return 'completed';
        if (phases.includes('in_production')) return 'in_production';
        if (phases.includes('cutting_ended')) return 'cutting_ended';
        if (phases.includes('in_cutting')) return 'in_cutting';
        return 'not_started';
    };

    const getQuoteIcon = (quoteType) => {
        switch (quoteType) {
            case 'roof': return <Home size={14} className="mr-2" />;
            case 'wall': return <BrickWall size={14} className="mr-2" />;
            case 'floor': return <Grid2X2 size={14} className="mr-2" />;
            default: return <Layers size={14} className="mr-2" />;
        }
    };

    const renderAssigneeInfo = () => (
        <div className="p-3 max-w-xs">
            <h4 className="font-semibold text-sm mb-3 text-gray-900">Assignment Information</h4>

            {/* Design Quotes */}
            <div className="space-y-2 mb-3">
                <h5 className="text-xs font-medium text-gray-700">Design:</h5>
                {['roof', 'wall', 'floor'].map(quoteType => {
                    if (!hasQuoteType(quoteType)) return null;
                    const designer = getCurrentDesigner(quoteType);
                    const isCompleted = isDesignCompleted(quoteType);

                    return (
                        <div key={quoteType} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                                {quoteType === 'roof' && <Home size={12} />}
                                {quoteType === 'wall' && <BrickWall size={12} />}
                                {quoteType === 'floor' && <Grid2X2 size={12} />}
                                <span className="capitalize font-medium">{quoteType}:</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-gray-600">
                                    {designer ? designer.name : 'Unassigned'}
                                </span>
                                {isCompleted && <CheckCircle size={12} className="text-green-500" />}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Estimation */}
            {(assignment.jobType === 'Estimation' || assignment.jobType === 'Design & Estimation') && (
                <div className="space-y-2 mb-3">
                    <h5 className="text-xs font-medium text-gray-700">Estimation:</h5>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">Estimator:</span>
                        <div className="flex items-center gap-1">
                            <span className="text-gray-600">
                                {assignment.estimator?.name || 'Unassigned'}
                            </span>
                            {assignment.estimator?.isCompleted && <CheckCircle size={12} className="text-green-500" />}
                        </div>
                    </div>
                </div>
            )}

            {/* Production */}
            {assignment.production && assignment.production.userId && (
                <div className="border-t pt-2">
                    <h5 className="text-xs font-medium text-gray-700 mb-1">Production:</h5>
                    <div className="flex items-center justify-between text-xs">
                        <span className="font-medium">Lead:</span>
                        <span className="text-gray-600">{assignment.production.name}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1">
                        <span className="font-medium">Phase:</span>
                        <span className={`px-1 py-0.5 rounded text-xs ${assignment.production.phase === 'completed' ? 'bg-green-100 text-green-700' :
                            assignment.production.phase === 'in_production' ? 'bg-blue-100 text-blue-700' :
                                assignment.production.phase === 'cutting_ended' ? 'bg-purple-100 text-purple-700' :
                                    assignment.production.phase === 'in_cutting' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-gray-100 text-gray-700'
                            }`}>
                            {assignment.production.phase === 'in_cutting' ? 'In Cutting' :
                                assignment.production.phase === 'cutting_ended' ? 'Cutting Ended' :
                                    assignment.production.phase === 'in_production' ? 'In Production' :
                                        assignment.production.phase === 'completed' ? 'Completed' :
                                            'Not Started'}
                        </span>
                    </div>
                    {assignment.production.startCuttingAt && (
                        <div className="flex items-center justify-between text-xs mt-1">
                            <span className="font-medium">Cutting Started:</span>
                            <span className="text-gray-600">
                                {new Date(assignment.production.startCuttingAt).toLocaleDateString('en-AU')}
                            </span>
                        </div>
                    )}
                    {assignment.production.startProductionAt && (
                        <div className="flex items-center justify-between text-xs mt-1">
                            <span className="font-medium">Production Started:</span>
                            <span className="text-gray-600">
                                {new Date(assignment.production.startProductionAt).toLocaleDateString('en-AU')}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    const renderDesignerSubmenu = (quoteType, icon) => {
        const isCompleted = isDesignCompleted(quoteType);
        const currentDesigner = getCurrentDesigner(quoteType);

        if (isCompleted && currentDesigner) {
            // Show completed designer info (non-interactive)
            return (
                <ContextMenu.Sub key={quoteType}>
                    <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                        {icon}
                        <span className="capitalize">{quoteType}</span>
                        <CheckCircle size={14} className="ml-auto text-green-500" />
                    </ContextMenu.SubTrigger>
                    <ContextMenu.Portal>
                        <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                            <div className="p-3 text-center">
                                <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
                                <div className="font-semibold text-sm text-gray-900">
                                    {currentDesigner.name}
                                </div>
                                <div className="text-xs text-green-600 font-medium mt-1">
                                    Design Completed
                                </div>
                            </div>
                        </ContextMenu.SubContent>
                    </ContextMenu.Portal>
                </ContextMenu.Sub>
            );
        }

        // Show normal assignment menu for incomplete designs
        return (
            <ContextMenu.Sub key={quoteType}>
                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                    {icon}
                    <span className="capitalize">{quoteType}</span>
                    <div className="context-menu-arrow">▶</div>
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                        {availableDesigners.length > 0 ? (
                            availableDesigners.map((designer) => {
                                const isCurrent = currentDesigner?.userId === designer.userId;
                                return (
                                    <ContextMenu.Item
                                        key={designer.userId}
                                        className={`context-menu-item ${isCurrent ? 'context-menu-current' : ''}`}
                                        onSelect={() => onAssignDesigner(assignment._originalId || assignment._id, quoteType, designer.userId, designer.username)}
                                    >
                                        <span>{designer.username}</span>
                                        {isCurrent && <span className="context-menu-badge">Current</span>}
                                    </ContextMenu.Item>
                                );
                            })
                        ) : (
                            <ContextMenu.Item className="context-menu-item" disabled>
                                No designers available
                            </ContextMenu.Item>
                        )}
                    </ContextMenu.SubContent>
                </ContextMenu.Portal>
            </ContextMenu.Sub>
        );
    };

    // Render direct designer list for separated view
    const renderDirectDesignerList = () => {
        const isCompleted = isDesignCompleted(separatedQuoteType);
        const currentDesigner = getCurrentDesigner(separatedQuoteType);

        if (isCompleted && currentDesigner) {
            // Show completed status - non-interactive
            return (
                <div className="p-3 text-center">
                    <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
                    <div className="font-semibold text-sm text-gray-900">
                        {currentDesigner.name}
                    </div>
                    <div className="text-xs text-green-600 font-medium mt-1">
                        Design Completed
                    </div>
                </div>
            );
        }

        // Show assignable designers
        return (
            <>
                {availableDesigners.length > 0 ? (
                    availableDesigners.map((designer) => {
                        const isCurrent = currentDesigner?.userId === designer.userId;
                        return (
                            <ContextMenu.Item
                                key={designer.userId}
                                className={`context-menu-item ${isCurrent ? 'context-menu-current' : ''}`}
                                onSelect={() => onAssignDesigner(assignment._originalId || assignment._id, separatedQuoteType, designer.userId, designer.username)}
                            >
                                <span>{designer.username}</span>
                                {isCurrent && <span className="context-menu-badge">Current</span>}
                            </ContextMenu.Item>
                        );
                    })
                ) : (
                    <ContextMenu.Item className="context-menu-item" disabled>
                        No designers available
                    </ContextMenu.Item>
                )}
            </>
        );
    };

    // Render production action with quote type selection for combined view
    const renderProductionQuoteSubmenu = (action, actionLabel, icon) => {
        const requiredQuotes = ['roof', 'wall', 'floor'].filter(type => hasQuoteType(type));

        if (requiredQuotes.length === 0) return null;

        return (
            <ContextMenu.Sub>
                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                    {icon}
                    {actionLabel}
                    <div className="context-menu-arrow">▶</div>
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                        {requiredQuotes.map(quoteType => (
                            <ContextMenu.Item
                                key={quoteType}
                                className="context-menu-item"
                                onSelect={() => {
                                    const assignmentId = assignment._originalId || assignment._id;
                                    console.log('Context menu action:', { action, quoteType, assignmentId }); // Debug log

                                    if (action.includes('start')) {
                                        onStartProduction && onStartProduction(assignmentId, action, quoteType);
                                    } else {
                                        onEndProduction && onEndProduction(assignmentId, action, quoteType);
                                    }
                                }}
                            >
                                {getQuoteIcon(quoteType)}
                                <span className="capitalize">{quoteType}</span>
                            </ContextMenu.Item>
                        ))}
                    </ContextMenu.SubContent>
                </ContextMenu.Portal>
            </ContextMenu.Sub>
        );
    };

    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger asChild>
                {children}
            </ContextMenu.Trigger>

            <ContextMenu.Portal>
                <ContextMenu.Content className="context-menu-content">
                    {/* Job Info Header - Show quote type if separated */}
                    <div className="context-menu-header">
                        <div className="font-semibold text-gray-900">
                            {assignment.uniqueId}
                            {isSeparated && (
                                <span className="ml-2 text-xs font-medium text-blue-600 capitalize">
                                    ({separatedQuoteType})
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-gray-500">ID: {assignment._originalId || assignment._id}</div>
                    </div>

                    <ContextMenu.Separator className="context-menu-separator" />

                    {/* Open Assignment */}
                    <ContextMenu.Item
                        className="context-menu-item"
                        onSelect={() => onOpenAssignment(assignment._originalId || assignment._id)}
                    >
                        <ExternalLink size={14} className="mr-2" />
                        Open Assignment
                    </ContextMenu.Item>

                    {/* Edit Assignment */}
                    <ContextMenu.Item
                        className="context-menu-item"
                        onSelect={() => onEditAssignment(assignment._originalId || assignment._id)}
                    >
                        <Edit size={14} className="mr-2" />
                        Edit Assignment
                    </ContextMenu.Item>

                    <ContextMenu.Separator className="context-menu-separator" />

                    {/* Page-specific content */}
                    {page === 'production' ? (
                        <>
                            {/* See Assignee - Production Page */}
                            <ContextMenu.Sub>
                                <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                    <Info size={14} className="mr-2" />
                                    See Assignee
                                    <div className="context-menu-arrow">▶</div>
                                </ContextMenu.SubTrigger>
                                <ContextMenu.Portal>
                                    <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                        {renderAssigneeInfo()}
                                    </ContextMenu.SubContent>
                                </ContextMenu.Portal>
                            </ContextMenu.Sub>

                            {/* Production Control */}
                            <ContextMenu.Separator className="context-menu-separator" />

                            {(() => {
                                // For combined view, show disabled message
                                if (!isSeparated) {
                                    return (
                                        <ContextMenu.Sub>
                                            <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                                <Info size={14} className="mr-2" />
                                                Production Controls
                                                <div className="context-menu-arrow">▶</div>
                                            </ContextMenu.SubTrigger>
                                            <ContextMenu.Portal>
                                                <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                                    <div className="p-3 max-w-xs text-center">
                                                        <Info size={24} className="mx-auto text-blue-500 mb-2" />
                                                        <div className="font-semibold text-sm text-gray-900 mb-1">
                                                            Production Controls Disabled
                                                        </div>
                                                        <div className="text-xs text-gray-600">
                                                            Please use <span className='text-blue-600 font-semibold'>separated view</span> to control production for individual quote types
                                                        </div>
                                                    </div>
                                                </ContextMenu.SubContent>
                                            </ContextMenu.Portal>
                                        </ContextMenu.Sub>
                                    );
                                }

                                // For separated view, show normal production controls
                                const status = getProductionStatus();

                                switch (status) {
                                    case 'not_started':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onStartProduction && onStartProduction(assignment._originalId || assignment._id, 'start_cutting', separatedQuoteType)}
                                            >
                                                <Play size={14} className="mr-2" />
                                                Start Cutting
                                            </ContextMenu.Item>
                                        );

                                    case 'in_cutting':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onEndProduction && onEndProduction(assignment._originalId || assignment._id, 'end_cutting', separatedQuoteType)}
                                            >
                                                <Square size={14} className="mr-2" />
                                                End Cutting
                                            </ContextMenu.Item>
                                        );

                                    case 'cutting_ended':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onStartProduction && onStartProduction(assignment._originalId || assignment._id, 'start_production', separatedQuoteType)}
                                            >
                                                <Play size={14} className="mr-2" />
                                                Start Production
                                            </ContextMenu.Item>
                                        );

                                    case 'in_production':
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onEndProduction && onEndProduction(assignment._originalId || assignment._id, 'end_production', separatedQuoteType)}
                                            >
                                                <Square size={14} className="mr-2" />
                                                End Production
                                            </ContextMenu.Item>
                                        );

                                    case 'completed':
                                        return (
                                            <ContextMenu.Item className="context-menu-item" disabled>
                                                <CheckCircle size={14} className="mr-2 text-green-500" />
                                                Production Completed
                                            </ContextMenu.Item>
                                        );

                                    default:
                                        return (
                                            <ContextMenu.Item
                                                className="context-menu-item"
                                                onSelect={() => onStartProduction && onStartProduction(assignment._originalId || assignment._id, 'start_cutting', separatedQuoteType)}
                                            >
                                                <Play size={14} className="mr-2" />
                                                Start Cutting
                                            </ContextMenu.Item>
                                        );
                                }
                            })()}
                        </>
                    ) : (
                        /* Assign Designers - Editor Page */
                        isSeparated ? (
                            /* SEPARATED VIEW: Direct designer assignment for the specific quote type */
                            hasQuoteType(separatedQuoteType) && (
                                <ContextMenu.Sub>
                                    <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                        {getQuoteIcon(separatedQuoteType)}
                                        <span className="capitalize">Assign Designer</span>
                                        <div className="context-menu-arrow">▶</div>
                                    </ContextMenu.SubTrigger>
                                    <ContextMenu.Portal>
                                        <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                            {renderDirectDesignerList()}
                                        </ContextMenu.SubContent>
                                    </ContextMenu.Portal>
                                </ContextMenu.Sub>
                            )
                        ) : (
                            /* COMBINED VIEW: Traditional nested menu for all quote types */
                            (hasQuoteType('roof') || hasQuoteType('wall') || hasQuoteType('floor')) && (
                                <ContextMenu.Sub>
                                    <ContextMenu.SubTrigger className="context-menu-item context-menu-subtrigger">
                                        <Users size={14} className="mr-2" />
                                        Assign Designers
                                        <div className="context-menu-arrow">▶</div>
                                    </ContextMenu.SubTrigger>

                                    <ContextMenu.Portal>
                                        <ContextMenu.SubContent className="context-menu-content context-menu-subcontent">
                                            {hasQuoteType('roof') && renderDesignerSubmenu('roof', <Home size={14} className="mr-2" />)}
                                            {hasQuoteType('wall') && renderDesignerSubmenu('wall', <BrickWall size={14} className="mr-2" />)}
                                            {hasQuoteType('floor') && renderDesignerSubmenu('floor', <Grid2X2 size={14} className="mr-2" />)}
                                        </ContextMenu.SubContent>
                                    </ContextMenu.Portal>
                                </ContextMenu.Sub>
                            )
                        )
                    )}
                </ContextMenu.Content>
            </ContextMenu.Portal>
        </ContextMenu.Root>
    );
};

export default AssignmentContextMenu;